import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  Save,
  Calendar,
  Users,
  ClipboardList,
  Sparkles,
  Tag,
  Clock,
  CircleDot,
  IndianRupee,
  Loader2,
} from "lucide-react";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import apiCall from "../../../../Redux/ApiCalls/AuthorizedApi";
import {
  getOrderSessionsAPI,
  getTeamMemberAvailabilityAPI,
  getOrderByIdAPI,
  getOrderWorkAssignmentsAPI,
} from "../../../../Redux/ApiCalls/Dashboard/OrderAPI";
import { getEventTypesAPI } from "../../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import { OrderWorkRecord } from "./types";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  FieldFull,
  Label,
  Input,
  Select,
  FormLabelIcon,
  FormGradientDefs,
  RequiredStar,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

type ModalProps = {
  record?: OrderWorkRecord | null;
  orders: any[];
  employees: any[];
  existingWorkAssignments?: any[];
  defaultOrderNo?: string;
  onSave: (data: any) => void;
  onClose: () => void;
  isSaving?: boolean;
};

const DEFAULT_SESSIONS = [
  { value: "FULLDAY", label: "Full Day" },
  { value: "HALFDAY", label: "Half Day" },
  { value: "MORNING", label: "Morning" },
  { value: "AFTERNOON", label: "Afternoon" },
  { value: "EVENING", label: "Evening" },
  { value: "NIGHT", label: "Night" },
];

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 24px 24px;
  flex-shrink: 0;
`;

const WhatsAppRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
`;

const WhatsAppLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.7);
  cursor: pointer;
`;

const WhatsAppCheckbox = styled.input.attrs({ type: "checkbox" })`
  accent-color: #c94daa;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FooterPrimaryBtn = styled(PrimaryBtn)`
  width: auto;
  min-width: 160px;
`;

const AvailabilityHint = styled.span`
  margin-left: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
  text-transform: none;
  letter-spacing: 0;
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgba(23, 23, 23, 0.5);
  margin-bottom: 4px;
`;

const AssignWorkModal: React.FC<ModalProps> = ({
  record,
  orders,
  employees,
  existingWorkAssignments = [],
  defaultOrderNo,
  onSave,
  onClose,
  isSaving = false,
}) => {
  const initialEmployeeIds = useMemo(() => {
    if (record?.teamMembers && record.teamMembers.length > 0) {
      return record.teamMembers;
    }
    if (record?.employeeName) {
      const names = record.employeeName.split(",").map(n => n.trim().toLowerCase());
      return employees
        .filter(emp => names.includes(emp.name.toLowerCase()))
        .map(emp => emp.id);
    }
    return [];
  }, [record, employees]);

  const [sessionsList, setSessionsList] = useState<{ value: string; label: string }[]>(DEFAULT_SESSIONS);
  const [quotationEventTypes, setQuotationEventTypes] = useState<string[]>([]);
  const [orderEvents, setOrderEvents] = useState<any[]>([]);
  const [selectedOrderEventId, setSelectedOrderEventId] = useState<number | string | null>(null);
  const [allEventsAssigned, setAllEventsAssigned] = useState(false);
  const [fetchedAssignments, setFetchedAssignments] = useState<any[]>([]);

  const [form, setForm] = useState({
    orderNo: record?.orderNo ?? defaultOrderNo ?? "",
    selectedEmployeeIds: initialEmployeeIds,
    eventDate: record?.eventDate ?? new Date().toISOString().split("T")[0],
    eventSession: record?.eventSession ?? "FULLDAY",
    eventType: record?.eventType ?? "",
    status: record?.status ?? "Assigned",
  });

  const [notifyWhatsApp, setNotifyWhatsApp] = useState(false);
  const [availabilityMap, setAvailabilityMap] = useState<{
    [id: number]: { available: boolean; conflict: any };
  }>({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const [employeeRoles, setEmployeeRoles] = useState<{ [id: number]: string }>(() => {
    if (record?.assignedMembers && record.assignedMembers.length > 0) {
      const initialRoles: { [id: number]: string } = {};
      record.assignedMembers.forEach(m => {
        initialRoles[m.employee] = m.role;
      });
      return initialRoles;
    }
    if (record && record.teamMembers && record.teamMembers.length > 0) {
      return { [record.teamMembers[0]]: record.role };
    }
    if (record && record.employeeName) {
      const names = record.employeeName.split(",").map(n => n.trim().toLowerCase());
      const matched = employees.find(emp => names.includes(emp.name.toLowerCase()));
      if (matched) {
        return { [matched.id]: record.role };
      }
    }
    return {};
  });

  const [employeeAmounts, setEmployeeAmounts] = useState<{ [id: number]: string }>(() => {
    if (record?.assignedMembers && record.assignedMembers.length > 0) {
      const initialAmounts: { [id: number]: string } = {};
      record.assignedMembers.forEach(m => {
        initialAmounts[m.employee] = String(m.amount);
      });
      return initialAmounts;
    }
    if (record && record.teamMembers && record.teamMembers.length > 0) {
      return { [record.teamMembers[0]]: record.amount };
    }
    if (record && record.employeeName) {
      const names = record.employeeName.split(",").map(n => n.trim().toLowerCase());
      const matched = employees.find(emp => names.includes(emp.name.toLowerCase()));
      if (matched) {
        return { [matched.id]: record.amount };
      }
    }
    return {};
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const allAssignments = useMemo(() => {
    const combined = [...existingWorkAssignments, ...fetchedAssignments];
    const uniqueMap = new Map();
    combined.forEach(item => {
      if (item && item.id) uniqueMap.set(String(item.id), item);
    });
    return Array.from(uniqueMap.values());
  }, [existingWorkAssignments, fetchedAssignments]);

  useEffect(() => {
    getOrderSessionsAPI()
      .then((res: any) => {
        if (res && Array.isArray(res.sessions) && res.sessions.length > 0) {
          setSessionsList(res.sessions.map((s: any) => ({ value: s.value, label: s.label || s.value })));
        }
      })
      .catch(() => {});

    getEventTypesAPI()
      .then((res: any) => {
        const list = res?.results || (Array.isArray(res) ? res : []);
        if (Array.isArray(list)) {
          setQuotationEventTypes(list.map((et: any) => et.name || et.event_type || "").filter(Boolean));
        }
      })
      .catch(() => {});

    getOrderWorkAssignmentsAPI({ page_size: 200 })
      .then((res: any) => {
        const list = res?.results || (Array.isArray(res) ? res : []);
        if (Array.isArray(list)) {
          setFetchedAssignments(list);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const set = (field: string, val: any) => setForm((p) => ({ ...p, [field]: val }));

  const getValidSession = (candidate?: string, fallback: string = "FULLDAY") => {
    if (!candidate) return fallback;
    const upper = String(candidate).trim().toUpperCase();
    const isKnown = sessionsList.some(s => s.value.toUpperCase() === upper) || DEFAULT_SESSIONS.some(s => s.value.toUpperCase() === upper);
    return isKnown ? upper : fallback;
  };

  const getAssignmentsForOrder = (orderNoStr: string, orderId?: any) => {
    const targetNo = String(orderNoStr || "").toLowerCase().trim();
    return allAssignments.filter((wa: any) => {
      if (record?.id && String(wa.id) === String(record.id)) return false;

      const waNo = String(wa.orderNo || wa.order_no || "").toLowerCase().trim();
      if (waNo && targetNo && waNo === targetNo) return true;

      if (orderId !== undefined && orderId !== null) {
        const waId = wa.orderId !== undefined ? wa.orderId : wa.order;
        if (waId !== undefined && waId !== null && String(waId) === String(orderId)) return true;
      }
      return false;
    });
  };

  const loadOrderDetails = (orderNoStr: string) => {
    if (!orderNoStr) return;
    const selectedOrder = orders.find(o => o.order_no.toLowerCase().trim() === orderNoStr.toLowerCase().trim());
    if (!selectedOrder) return;

    getOrderByIdAPI(selectedOrder.id)
      .then((orderRes: any) => {
        const orderAssignments = getAssignmentsForOrder(orderNoStr, selectedOrder.id);
        const rawEvents = Array.isArray(orderRes?.events) ? orderRes.events : [];

        if (rawEvents.length > 0) {
          const annotatedEvents = rawEvents.map((evt: any) => {
            const matchedWa = orderAssignments.find((wa: any) => {
              if (wa.order_event && evt.id && String(wa.order_event) === String(evt.id)) return true;

              const waType = String(wa.eventType || wa.event_type || "").toLowerCase().trim();
              const evtType = String(evt.event_type || evt.event_type_name || "").toLowerCase().trim();
              if (waType && evtType && (waType === evtType || waType.includes(evtType) || evtType.includes(waType))) return true;

              const waDate = String(wa.eventDate || wa.event_date || "").split("T")[0].trim();
              const evtDate = String(evt.event_date || "").split("T")[0].trim();
              if (waDate && evtDate && waDate === evtDate) return true;

              return false;
            });

            return {
              ...evt,
              isAssigned: !!matchedWa,
              assignedDetails: matchedWa ? `Assigned (${matchedWa.employee_name || 'Staff'})` : null
            };
          });

          const isAllAssigned = annotatedEvents.every((e: any) => e.isAssigned);
          if (isAllAssigned && !record) {
            showErrorToast(`Work has already been assigned for all events in order ${orderNoStr}`);
            setForm(p => ({ ...p, orderNo: "" }));
            setOrderEvents([]);
            setSelectedOrderEventId(null);
            setAllEventsAssigned(true);
            return;
          }

          setOrderEvents(annotatedEvents);

          let targetEvent: any = null;
          if (record) {
            targetEvent = annotatedEvents.find((e: any) =>
              (record.orderEvent && String(e.id) === String(record.orderEvent)) ||
              (record.eventType && String(e.event_type || e.event_type_name || "").toLowerCase() === String(record.eventType).toLowerCase()) ||
              (record.eventDate && String(e.event_date || "").split("T")[0] === String(record.eventDate).split("T")[0])
            );
          }
          if (!targetEvent) {
            const firstUnassigned = annotatedEvents.find((e: any) => !e.isAssigned);
            targetEvent = firstUnassigned || annotatedEvents[0];
          }

          if (targetEvent?.id) {
            setSelectedOrderEventId(targetEvent.id);
          }

          if (record) {
            setForm(p => ({
              ...p,
              orderNo: orderNoStr,
              eventDate: record.eventDate || targetEvent?.event_date || orderRes.order_date || p.eventDate,
              eventSession: record.eventSession ? record.eventSession.trim().toUpperCase() : (p.eventSession || "FULLDAY"),
              eventType: record.eventType || targetEvent?.event_type || targetEvent?.event_type_name || orderRes.function_details || p.eventType,
            }));
          } else {
            const sessionCandidate = targetEvent?.event_session || targetEvent?.event_time;
            const sessionVal = getValidSession(sessionCandidate, "FULLDAY");
            setForm(p => ({
              ...p,
              orderNo: orderNoStr,
              eventDate: targetEvent?.event_date || orderRes.order_date || p.eventDate,
              eventSession: sessionVal,
              eventType: targetEvent?.event_type || targetEvent?.event_type_name || orderRes.function_details || p.eventType,
            }));
          }
          setAllEventsAssigned(false);
        } else {
          const isSingleEventAssigned = orderAssignments.length > 0;
          if (isSingleEventAssigned && !record) {
            showErrorToast(`Work has already been assigned for all events in order ${orderNoStr}`);
            setForm(p => ({ ...p, orderNo: "" }));
            setOrderEvents([]);
            setSelectedOrderEventId(null);
            setAllEventsAssigned(true);
            return;
          }

          setOrderEvents([]);
          setSelectedOrderEventId(null);
          setAllEventsAssigned(false);
          if (record) {
            setForm(p => ({
              ...p,
              orderNo: orderNoStr,
              eventDate: record.eventDate || orderRes.order_date || p.eventDate,
              eventSession: record.eventSession ? record.eventSession.trim().toUpperCase() : (p.eventSession || "FULLDAY"),
              eventType: record.eventType || orderRes.function_details || orderRes.event_type || p.eventType,
            }));
          } else {
            setForm(p => ({
              ...p,
              orderNo: orderNoStr,
              eventDate: orderRes.order_date || p.eventDate,
              eventType: orderRes.function_details || orderRes.event_type || p.eventType,
            }));
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (form.orderNo) {
      loadOrderDetails(form.orderNo);
    }
  }, [form.orderNo, allAssignments.length]);

  const handleOrderChange = (orderNo: string) => {
    if (!orderNo) {
      setForm(p => ({ ...p, orderNo: "" }));
      setOrderEvents([]);
      setSelectedOrderEventId(null);
      setAllEventsAssigned(false);
      return;
    }
    loadOrderDetails(orderNo);
  };

  const handleEventChange = (eventIdStr: string) => {
    if (!eventIdStr) {
      setSelectedOrderEventId(null);
      return;
    }
    const eventId = Number(eventIdStr);
    const matched = orderEvents.find(e => e.id === eventId);
    if (matched) {
      setSelectedOrderEventId(matched.id);
      const sessionCandidate = matched.event_session || matched.event_time;
      const sessionVal = getValidSession(sessionCandidate, form.eventSession || "FULLDAY");
      setForm(p => ({
        ...p,
        eventDate: matched.event_date || p.eventDate,
        eventSession: sessionVal,
        eventType: matched.event_type || matched.event_type_name || p.eventType,
      }));
    }
  };

  useEffect(() => {
    if (!form.eventDate || !form.eventSession) return;
    setCheckingAvailability(true);
    getTeamMemberAvailabilityAPI({
      event_date: form.eventDate,
      event_session: form.eventSession,
      order_event: selectedOrderEventId || undefined,
      exclude_assignment: record?.id || undefined,
    })
      .then((res: any) => {
        if (res && Array.isArray(res.members)) {
          const map: { [id: number]: { available: boolean; conflict: any } } = {};
          res.members.forEach((m: any) => {
            map[m.id] = { available: m.available, conflict: m.conflict };
          });
          setAvailabilityMap(map);
        }
      })
      .catch(() => {})
      .finally(() => setCheckingAvailability(false));
  }, [form.eventDate, form.eventSession, selectedOrderEventId, record?.id]);

  const handleSubmit = () => {
    if (!form.orderNo.trim()) { showErrorToast("Order number is required"); return; }
    if (form.selectedEmployeeIds.length === 0) { showErrorToast("At least one employee is required"); return; }
    if (!form.eventDate) { showErrorToast("Event date is required"); return; }

    const matchOrder = orders.find(o => o.order_no.toLowerCase().trim() === form.orderNo.toLowerCase().trim());
    if (!matchOrder) {
      showErrorToast(`Order number ${form.orderNo} not found`);
      return;
    }

    const orderAssignments = getAssignmentsForOrder(form.orderNo, matchOrder.id);

    if (allEventsAssigned && !record) {
      showErrorToast(`Work has already been assigned for all events in order ${form.orderNo}.`);
      return;
    }

    const duplicateAssignment = orderAssignments.find((wa: any) => {
      if (selectedOrderEventId && wa.order_event && String(wa.order_event) === String(selectedOrderEventId)) return true;
      const waType = String(wa.eventType || wa.event_type || "").toLowerCase().trim();
      const formType = String(form.eventType || "").toLowerCase().trim();
      if (waType && formType && (waType === formType || waType.includes(formType) || formType.includes(waType))) return true;
      const waDate = String(wa.eventDate || wa.event_date || "").split("T")[0].trim();
      const formDate = String(form.eventDate || "").split("T")[0].trim();
      if (waDate && formDate && waDate === formDate) return true;
      return false;
    });

    if (duplicateAssignment && !record) {
      showErrorToast(`Work has already been assigned for this event in order ${form.orderNo}. Please edit the existing assignment instead.`);
      return;
    }

    for (const empId of form.selectedEmployeeIds) {
      const availInfo = availabilityMap[empId];
      if (availInfo && availInfo.available === false) {
        const emp = employees.find(e => e.id === empId);
        const conflict = availInfo.conflict;
        const conflictMsg = conflict
          ? `Already assigned to order ${conflict.order_no || ""} (${conflict.event_session || ""}).`
          : "Not available for this slot.";
        showErrorToast(`${emp?.name || "Selected employee"} is not available on ${form.eventDate} (${form.eventSession}). ${conflictMsg}`);
        return;
      }
    }

    const assigned_members = form.selectedEmployeeIds.map(empId => ({
      team_member: empId,
      role: employeeRoles[empId] || "Staff",
      amount: Number(employeeAmounts[empId]) || 0
    }));

    const payload = {
      order: matchOrder.id,
      order_event: selectedOrderEventId || undefined,
      assigned_members,
      team_members: form.selectedEmployeeIds,
      employee_name: form.selectedEmployeeIds.map(id => employees.find(e => e.id === id)?.name || "").join(", "),
      event_date: form.eventDate,
      event_session: form.eventSession,
      event_type: form.eventType || "Event",
      role: assigned_members[0]?.role || "Staff",
      amount: Number(assigned_members[0]?.amount) || 0,
      status: form.status,
      notify_whatsapp: notifyWhatsApp,
    };
    onSave(payload);
  };

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Work assignment
                </HeroBadge>
                <ModalTitle>{record ? "Edit Work Assignment" : "Assign New Work"}</ModalTitle>
                <ModalSubtitle>
                  {record
                    ? "Update team assignment details for this order event."
                    : "Assign team members to an order event with roles and wages."}
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            <FormCard>
              <FieldGrid>
                <FieldFull as="div">
                  <Label htmlFor="assign-work-order">
                    <FormLabelIcon icon={ClipboardList} />
                    Order No
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <Select
                    id="assign-work-order"
                    value={form.orderNo}
                    onChange={(e) => handleOrderChange(e.target.value)}
                  >
                    <option value="">Select order</option>
                    {orders.map(o => (
                      <option key={o.id} value={o.order_no}>
                        {o.order_no} - {o.customer_name}
                      </option>
                    ))}
                  </Select>
                </FieldFull>

                {orderEvents.length > 0 && (
                  <FieldFull as="div">
                    <Label htmlFor="assign-work-event">
                      <FormLabelIcon icon={Calendar} />
                      Select Event
                    </Label>
                    <Select
                      id="assign-work-event"
                      value={selectedOrderEventId || ""}
                      onChange={(e) => handleEventChange(e.target.value)}
                    >
                      {orderEvents.map((evt: any) => (
                        <option key={evt.id} value={evt.id} disabled={evt.isAssigned && !record}>
                          {evt.isAssigned ? "✓ [Already Assigned] " : ""}{evt.event_type || evt.event_type_name || "Event"}{evt.event_date ? ` (${evt.event_date}${evt.event_time ? ` - ${evt.event_time}` : ""})` : ""}
                        </option>
                      ))}
                    </Select>
                  </FieldFull>
                )}

                <FieldFull as="div" ref={dropdownRef} style={{ position: "relative" }}>
                  <Label>
                    <FormLabelIcon icon={Users} />
                    Employees
                    <RequiredStar>*</RequiredStar>
                    {checkingAvailability && (
                      <AvailabilityHint>Checking availability...</AvailabilityHint>
                    )}
                  </Label>
                  <MultiSelectContainer>
                    <MultiSelectTrigger onClick={() => setDropdownOpen(!dropdownOpen)}>
                      {form.selectedEmployeeIds.length === 0 ? (
                        <MultiSelectPlaceholder>Select employees</MultiSelectPlaceholder>
                      ) : (
                        <MultiSelectValues>
                          {employees
                            .filter(e => form.selectedEmployeeIds.includes(e.id))
                            .map(e => (
                              <MultiSelectBadge key={e.id}>
                                {e.name}
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setForm(p => ({
                                      ...p,
                                      selectedEmployeeIds: p.selectedEmployeeIds.filter(id => id !== e.id)
                                    }));
                                  }}
                                >
                                  ×
                                </button>
                              </MultiSelectBadge>
                            ))}
                        </MultiSelectValues>
                      )}
                      <DropdownIcon />
                    </MultiSelectTrigger>

                    {dropdownOpen && (
                      <MultiSelectDropdown>
                        <DropdownSearch
                          placeholder="Search employees..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <DropdownOptionsList>
                          {employees
                            .filter(e => e.name.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map(e => {
                              const isSelected = form.selectedEmployeeIds.includes(e.id);
                              const availInfo = availabilityMap[e.id];
                              const isUnavailable = availInfo ? availInfo.available === false : false;
                              const conflict = availInfo?.conflict;

                              return (
                                <DropdownOption
                                  key={e.id}
                                  $selected={isSelected}
                                  $disabled={isUnavailable}
                                  onClick={() => {
                                    if (isUnavailable) {
                                      const conflictMsg = conflict
                                        ? `Already assigned to order ${conflict.order_no || ""} (${conflict.event_session || ""}).`
                                        : "Not available for this time slot.";
                                      showErrorToast(`${e.name} is not available on ${form.eventDate} (${form.eventSession}). ${conflictMsg}`);
                                      return;
                                    }
                                    if (isSelected) {
                                      setForm(p => ({
                                        ...p,
                                        selectedEmployeeIds: p.selectedEmployeeIds.filter(id => id !== e.id)
                                      }));
                                    } else {
                                      setForm(p => ({
                                        ...p,
                                        selectedEmployeeIds: [...p.selectedEmployeeIds, e.id]
                                      }));
                                      setEmployeeRoles(prev => ({
                                        ...prev,
                                        [e.id]: prev[e.id] || e.job_type || ""
                                      }));
                                    }
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <CheckboxInput
                                      checked={isSelected}
                                      disabled={isUnavailable}
                                      readOnly
                                    />
                                    <span style={{ fontWeight: isSelected ? 700 : 500 }}>{e.name}</span>
                                  </div>
                                  {isUnavailable && (
                                    <ConflictBadge title={`Assigned to ${conflict?.order_no || "Order"} (${conflict?.event_session || ""})`}>
                                      Booked ({conflict?.order_no || "Conflict"})
                                    </ConflictBadge>
                                  )}
                                </DropdownOption>
                              );
                            })}
                        </DropdownOptionsList>
                      </MultiSelectDropdown>
                    )}
                  </MultiSelectContainer>
                </FieldFull>

                <Field>
                  <Label htmlFor="assign-work-date">
                    <FormLabelIcon icon={Calendar} />
                    Event Date
                    <RequiredStar>*</RequiredStar>
                  </Label>
                  <BrandDatePicker
                    name="assign-work-date"
                    value={form.eventDate}
                    onChange={(v) => set("eventDate", v)}
                  />
                </Field>

                <Field>
                  <Label htmlFor="assign-work-session">
                    <FormLabelIcon icon={Clock} />
                    Session
                  </Label>
                  <Select
                    id="assign-work-session"
                    value={form.eventSession}
                    onChange={(e) => set("eventSession", e.target.value)}
                  >
                    {sessionsList.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <Label htmlFor="assign-work-event-type">
                    <FormLabelIcon icon={Tag} />
                    Event Name / Type
                  </Label>
                  <Input
                    id="assign-work-event-type"
                    placeholder="e.g. Wedding, Sangeet"
                    list="quotation-event-types"
                    value={form.eventType}
                    onChange={(e) => set("eventType", e.target.value)}
                  />
                  <datalist id="quotation-event-types">
                    {quotationEventTypes.map((et, idx) => (
                      <option key={idx} value={et} />
                    ))}
                  </datalist>
                </Field>

                <Field>
                  <Label htmlFor="assign-work-status">
                    <FormLabelIcon icon={CircleDot} />
                    Status
                  </Label>
                  <Select
                    id="assign-work-status"
                    value={form.status}
                    onChange={(e) => set("status", e.target.value)}
                  >
                    <option value="Assigned">Assigned</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                  </Select>
                </Field>
              </FieldGrid>
            </FormCard>

            {form.selectedEmployeeIds.length > 0 && (
              <RolesWagesSection>
                <SectionLabel>
                  <FormLabelIcon icon={IndianRupee} />
                  Roles & Wages
                </SectionLabel>
                <MembersGrid>
                  {form.selectedEmployeeIds.map(empId => {
                    const emp = employees.find(e => e.id === empId);
                    if (!emp) return null;
                    return (
                      <MemberRow key={empId}>
                        <MemberName>{emp.name}</MemberName>
                        <MemberInputs>
                          <Field style={{ margin: 0 }}>
                            <Label htmlFor={`role-${empId}`}>Role</Label>
                            <Input
                              id={`role-${empId}`}
                              placeholder="e.g. Photographer"
                              value={employeeRoles[empId] || ""}
                              onChange={(e) => setEmployeeRoles(prev => ({ ...prev, [empId]: e.target.value }))}
                            />
                          </Field>
                          <Field style={{ margin: 0 }}>
                            <Label htmlFor={`amount-${empId}`}>Amount (₹)</Label>
                            <Input
                              id={`amount-${empId}`}
                              type="number"
                              min={0}
                              placeholder="Wage amount"
                              value={employeeAmounts[empId] || ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                let sanitized = val;
                                if (val !== "") {
                                  const parsed = parseFloat(val);
                                  sanitized = isNaN(parsed) ? "" : String(Math.max(0, parsed));
                                }
                                setEmployeeAmounts(prev => ({ ...prev, [empId]: sanitized }));
                              }}
                            />
                          </Field>
                        </MemberInputs>
                      </MemberRow>
                    );
                  })}
                </MembersGrid>
              </RolesWagesSection>
            )}
          </ModalBody>

          <ModalFooter>
            <WhatsAppRow>
              <WhatsAppCheckbox
                id="notify_whatsapp"
                checked={notifyWhatsApp}
                onChange={(e) => setNotifyWhatsApp(e.target.checked)}
              />
              <WhatsAppLabel htmlFor="notify_whatsapp">
                Notify assigned member(s) on WhatsApp
              </WhatsAppLabel>
            </WhatsAppRow>
            <SecondaryBtn type="button" onClick={onClose} disabled={isSaving}>
              Cancel
            </SecondaryBtn>
            <FooterPrimaryBtn
              type="button"
              onClick={handleSubmit}
              disabled={(allEventsAssigned && !record) || isSaving}
            >
              {isSaving ? <Spinner size={16} /> : <Save size={16} />}
              {isSaving ? "Saving..." : record ? "Update Assignment" : "Save Assignment"}
            </FooterPrimaryBtn>
          </ModalFooter>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

export default AssignWorkModal;

const inputBase = `
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  padding: 8px 16px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #171717;
  background: #fafafa;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const MultiSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MultiSelectTrigger = styled.div`
  ${inputBase}
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  height: auto;
  flex-wrap: wrap;
`;

const MultiSelectValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const MultiSelectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;

  button {
    border: none;
    background: none;
    color: #c94daa;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    font-weight: bold;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    &:hover {
      color: #ff0000;
    }
  }
`;

const MultiSelectPlaceholder = styled.span`
  color: rgba(23, 23, 23, 0.35);
  font-weight: 500;
`;

const DropdownIcon = styled.span`
  border: solid rgba(201, 77, 170, 0.6);
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(45deg);
  margin-left: 8px;
  flex-shrink: 0;
`;

const MultiSelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  margin-top: 4px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 10001;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  padding: 8px;
`;

const DropdownSearch = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 10px;
  padding: 0 12px;
  margin-bottom: 8px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  outline: none;
  box-sizing: border-box;
  background: #fafafa;
  &:focus {
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
`;

const DropdownOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DropdownOption = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 500;
  opacity: ${p => p.$disabled ? 0.7 : 1};
  background: ${p => p.$selected ? "rgba(201,77,170,0.06)" : "transparent"};
  &:hover {
    background: ${p => p.$disabled ? "rgba(239, 68, 68, 0.05)" : "rgba(201,77,170,0.04)"};
  }
`;

const CheckboxInput = styled.input.attrs({ type: "checkbox" })`
  cursor: pointer;
  accent-color: #c94daa;
`;

const ConflictBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #dc2626;
  background: rgba(220, 38, 38, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`;

const RolesWagesSection = styled(FormCard)`
  margin-top: 0;
`;

const MembersGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MemberRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 12px;
  padding: 12px;
  @media (min-width: 600px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }
`;

const MemberName = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  min-width: 150px;
`;

const MemberInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex-grow: 1;
  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;
